<template>
	<SplashScreen v-if="isSplash" />
	<div class="wrapper">
		<TopHeader v-if="$route.name !== '404'" />
		<VueQueryDevtools />
		<RouterView />

		<BottomFooter
			v-if="
				$route.name !== '404' &&
				!($route.query.isMoreMenuOpen === 'true' && displayStore.isDesktop) &&
				!($route.name === 'sub/cancel-confirm' && !displayStore.isDesktop) &&
				!($route.name === 'sub/cancel-reason' && !displayStore.isDesktop) &&
				!($route.name === 'sub/cancel-complete' && !displayStore.isDesktop) &&
				!($route.name === 'payment-complete' && !displayStore.isDesktop) &&
				!($route.name === 'payment-failed' && !displayStore.isDesktop) &&
				!(lowerCase($route.path.split('/')[1]) === 'auth' && !displayStore.isDesktop) &&
				($route.name !== 'my' || !displayStore.isDesktop) &&
				!(lowerCase($route.path.split('/')[2]) === 'edit' && !displayStore.isDesktop) &&
				!($route.name === 'pw-change' && !displayStore.isDesktop)
			"
		/>

		<BottomNav
			v-if="
				$route.name !== '404' &&
				$route.path.split('/')[1] !== 'my' &&
				$route.path.split('/')[2] !== 'signup-complete' &&
				lowerCase($route.path.split('/')[1]) !== 'auth' &&
				lowerCase($route.path.split('/')[1]) !== 'sub' &&
				!($route.query.isMoreMenuOpen === 'true' && displayStore.isDesktop)
			"
		/>
	</div>
	<div id="modal"></div>
	<Popup v-if="popupStore.popup.content" />
	<LoginPopup v-if="loginPopupStore.popup.content" />
	<Toast v-if="toastStore.toast.content" />
	<DataLoading v-if="!isSplash" />
	<AppGuidePopup />
	<PageLoading />
	<GuideModal v-if="modalStore.checkModalOpen('GuideModal')" modal-name="GuideModal" title="대가의선택" is-close>
		<SubScribePopup />
	</GuideModal>
	<!-- 모바일 웹 앱 이동 -->
	<AppMove />
</template>

<script setup lang="ts">
import { onBeforeMount, onMounted, onUnmounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import { VueQueryDevtools } from '@tanstack/vue-query-devtools';
import axios from 'axios';
import { lowerCase } from 'lodash';

import BottomFooter from '@/components/layout/bottom/BottomFooter.vue';
import BottomNav from '@/components/layout/bottom/BottomNav.vue';
import SplashScreen from '@/components/layout/common/SplashScreen.vue';
import TopHeader from '@/components/layout/header/TopHeader.vue';
import PageLoading from '@/components/loading/PageLoading.vue';
import AppGuidePopup from '@/components/popup/AppGuidePopup.vue';
import LoginPopup from '@/components/popup/LoginPopup.vue';
import Popup from '@/components/popup/Popup.vue';
import Toast from '@/components/toast/Toast.vue';
import { useDisplayStore } from '@/stores/display';
import { useHeaderStore } from '@/stores/header';
// store
import { usePopupStore } from '@/stores/popup';
import { useToastStore } from '@/stores/toast';

import { fetchGetAppVersionApi } from './api/native';
// api
import { useCallbackMobileApi, useRequestTSignApi, useUpdatePushTokenApi } from './api/notification';
import { useGetMasterListDataApi } from './api/payment';
import DataLoading from './components/loading/DataLoading.vue';
import GuideModal from './components/modal/GuideModal.vue';
import SubScribePopup from './components/popup/SubScribePopup.vue';
// constant
import {
	ERROR_MEMBER_REGISTRATION_REQUIRED,
	MESSAGE_DOWNLOAD_COMPLETED,
	MESSAGE_DOWNLOAD_FAILED,
	MESSAGE_REQUIRED_UPDATE,
} from './constants/title';
import { useAuthStore } from './stores/auth';
import { useDeviceStore } from './stores/device';
import { useLoadingStore } from './stores/loading';
import { useLoginPopupStore } from './stores/LoginPopup';
import { useMobileDeviceStore } from './stores/mobileDevice';
import { useModalStore } from './stores/modal';
import { usePayInAppStore } from './stores/payInApp';
import { usePushStore } from './stores/push';
import { useTokenStore } from './stores/token';
// util
import {
	getPushPermissionYn,
	getPushToken,
	requestPermissionNotification,
} from './utils/directive/composables/usePushToken';
import { getAppVersion, isDeviceAOS, isDeviceIOS, openNativeAppStore } from './utils/native';
import AppMove from './components/AppMove.vue';

const loginPopupStore = useLoginPopupStore();
const popupStore = usePopupStore();
const toastStore = useToastStore();
const pushStore = usePushStore();
const displayStore = useDisplayStore();
const tokenStore = useTokenStore();
const authStore = useAuthStore();
const payStore = usePayInAppStore();
const loadingStore = useLoadingStore();
const deviceStore = useDeviceStore();
const headerStore = useHeaderStore();
const modalStore = useModalStore();
const mobileDeviceStore = useMobileDeviceStore();
const router = useRouter();
const { mutateAsync: callbackMobile } = useCallbackMobileApi();
const { mutateAsync: requestTSign } = useRequestTSignApi();
const { mutateAsync: updatePushToken } = useUpdatePushTokenApi();

// Splash
const isSplash = ref(true);
let isPageLoaded = true;
let isTimerFinished = false;

// 최소 1500ms
let toId = setTimeout(() => {
	isTimerFinished = true;
	if (!isPageLoaded) {
		// 로드가 먼저 끝난 경우
		isSplash.value = false;
	}
	// 아직 로드 중일 경우
	clearTimeout(toId);
}, 2000);

const checkDevice = () => {
	let agent = navigator.userAgent.toLowerCase();
	if (agent.indexOf('android') !== -1) {
		deviceStore.setDevice('MO');
	} else if (agent.indexOf('iphone') !== -1 || agent.indexOf('ipad') !== -1 || agent.indexOf('macintosh') !== -1) {
		deviceStore.setDevice('MO');
	} else {
		deviceStore.setDevice('PC');
	}
};

const checkMobileDevice = () => {
	let agent = navigator.userAgent.toLowerCase();
	if (agent.indexOf('android') !== -1) {
		mobileDeviceStore.setMobileDevice('AOS');
	} else if (agent.indexOf('iphone') !== -1 || agent.indexOf('ipad') !== -1 || agent.indexOf('macintosh') !== -1) {
		mobileDeviceStore.setMobileDevice('IOS');
	}
};

window.addEventListener('load', () => {
	if (!isTimerFinished) {
		// 아직 최소시간이 안 지난 경우, 빠른 환경
		isPageLoaded = false;
		return;
	}
	// 최소 시간이 지난 경우
	isSplash.value = false;
});

const { refetch: getMasterListRefetch } = useGetMasterListDataApi(false);

// native -> js interface
onBeforeMount(() => {
	checkDevice();
	headerStore.getCountry === '' ? headerStore.setCountry('KR') : null;
	checkMobileDevice();
	window.nativeInterface = {
		/**
		 * 앱 버전 받는 함수 (강제 업데이트 여부 체크)
		 * event : {version : string}
		 */
		nativeResultGetAppVersion: async (event: { code: number; name: string }) => {
			if (!event.name) return; // 현재 버전(1.0.0 형식) 업으면 return

			mobileDeviceStore.setAppVersion(event.name);

			// 앱 버전 확인 API
			const res = await fetchGetAppVersionApi();

			if (!res.data?.data.AOS || !res.data?.data.IOS) return; // 스토어 버전(1.0.0 형식) 없으면 return
			const currentVersion = event.name.split('.').map((item: string) => Number(item));
			const AOSVersion = res.data.data.AOS.split('.').map((item: string) => Number(item));
			const IOSVersion = res.data.data.IOS.split('.').map((item: string) => Number(item));

			const agent = navigator.userAgent.toLowerCase();

			// Android
			if (agent.indexOf('android') !== -1) {
				if (currentVersion[0] < AOSVersion[0] || currentVersion[1] < AOSVersion[1]) {
					popupStore.openPopup({
						content: MESSAGE_REQUIRED_UPDATE,
						cancel: () => {
							openNativeAppStore();
						},
					});
				}
			}

			// IOS
			if (agent.indexOf('iphone') !== -1 || agent.indexOf('ipad') !== -1 || agent.indexOf('macintosh') !== -1) {
				if (currentVersion[0] < IOSVersion[0] || currentVersion[1] < IOSVersion[1]) {
					popupStore.openPopup({
						content: MESSAGE_REQUIRED_UPDATE,
						cancel: () => {
							openNativeAppStore();
						},
					});
				}
			}
		},

		// push token 조회
		nativeResultGetPushToken: (event: { token: string }) => {
			// token 저장 API 호출
			pushStore.setPushData(event.token);
			updatePushToken({ member_push_token: event.token });
		},

		// push 허용 여부 요청
		nativeResultCheckPermissionNotification: (event: { enable: boolean }) => {
			if (!event.enable) {
				requestPermissionNotification();
			}
		},

		// social_uid 조회
		nativeResultSocialUpdate: async (event: { provider: string; uid: string; name: string }) => {
			// NONE : 로그인 되어있지 않음
			// GOOGLE : 구글로 로그인 됨
			// APPLE : 애플로 로그인 됨
			const res = await callbackMobile({ provider: event.provider.toLowerCase(), uid: event.uid });
			if (res.data.success) {
				const token = res.data.data.access_token;
				tokenStore.setAccessTokenForSNSSignUp(token);
				const resTSign = await requestTSign({ token: token });
				if (resTSign.data.success) {
					const {
						access_token,
						refresh_token,
						basic_info_yn,
						member_join_path,
						member_name,
						member_nickname,
						group_pk,
						group_sp,
					} = resTSign.data.data;
					authStore.setUserInfo({
						member_join_path: member_join_path,
					});
					if (!basic_info_yn) {
						alert(ERROR_MEMBER_REGISTRATION_REQUIRED);

						type QueryType = {
							joinPath: string;
							name?: string;
						};
						const query: QueryType = { joinPath: member_join_path };

						if (event.name) {
							query.name = event.name;
						}
						router.push({
							name: 'auth/terms-agreement',
							query: query,
						});
					} else {
						tokenStore.setAccessToken(access_token);
						authStore.setKeepLogin({ isKeepLogin: true });
						authStore.setUserInfo({
							member_name: member_name,
							member_nickname: member_nickname,
							member_join_path: member_join_path,
						});
						tokenStore.setToken(access_token, refresh_token);
						authStore.setUserType({
							group_pk: group_pk,
							group_sp: group_sp,
						});
						getPushToken();
						router.push('/');
					}
				}
			}
		},

		// 엑셀 다운로드
		nativeResultExcelDownload: (event: { success: string }) => {
			if (event.success === 'true') {
				alert(MESSAGE_DOWNLOAD_COMPLETED);
			} else {
				alert(MESSAGE_DOWNLOAD_FAILED);
			}
		},

		// 인앱 결제
		nativeResultInAppPurchase: async (event: { success: string; productId: string; token: string }) => {
			const params = {
				status_code: event.success === 'true' ? '0000' : '9999',
				temp_pay_pk: payStore.getTempPayPk,
				pay_info_pk: payStore.getPayInfoPk,
				transaction_id: event.productId,
				transaction_token: event.token,
				access_token: tokenStore.getAccessToken,
			};

			// if (ipData.value === MY_IP) {
			// 	alert(`인앱 결제 : ${JSON.stringify(event)}`);
			// }

			payStore.setPayToken(event.token);
			payStore.setProductId(event.productId);
			loadingStore.finishPageLoading();
			loadingStore.startPageLoading();
			if (event.success === 'true') {
				axios
					.post(`${import.meta.env.VITE_BASE_URL}/payNotiInApp`, params)
					.then((res) => {
						// alert(`payNotiInApp : ${JSON.stringify(res)}`);
						// if (ipData.value === MY_IP) {
						// 	alert(`payNotiInApp 성공 : ${JSON.stringify(res)}`);
						// }
						console.log(res);
						router.push('/payment-complete');
					})
					.catch((error) => {
						alert(`결제 완료중 오류가 발생했습니다`);
						// if (ipData.value === MY_IP) {
						// 	alert(`payNotiInApp catch : ${JSON.stringify(error)}`);
						// }
						console.error(error);
						router.push('/payment-failed');
					})
					.finally(() => {
						loadingStore.finishPageLoading();
						payStore.resetPayData();
					});
			} else {
				alert(`결제중 오류가 발생했습니다`);
				axios
					.post(`${import.meta.env.VITE_BASE_URL}/payNotiInApp`, params)
					.then((res) => {
						// alert(`payNotiInApp : ${JSON.stringify(res)}`);
						// if (ipData.value === MY_IP) {
						// 	alert(`payNotiInApp : ${JSON.stringify(res)}`);
						// }
						console.log('res:', res);
						router.push('/payment-failed');
					})
					.catch((error) => {
						alert(`결제 완료중 오류가 발생했습니다`);
						// alert(`payNotiInApp catch : ${JSON.stringify(error)}`);
						// if (ipData.value === MY_IP) {
						// 	alert(`payNotiInApp : ${JSON.stringify(error)}`);
						// }
						console.error('error:', error);
						router.push('/payment-failed');
					})
					.finally(() => {
						loadingStore.finishPageLoading();
						payStore.resetPayData();
						router.push('/payment-failed');
					});
			}
		},
		//구독 해지하고 앱으로 복귀 후, 구독 상황 최신화
		nativeResultOnResume: async () => {
			// if (ipData.value === MY_IP) {
			// 	alert(`resume 값 : ${JSON.stringify(event)}`);
			// }
			// alert(`앱으로 들어왔습니다. : ${JSON.stringify(event)}`);
			loadingStore.finishPageLoading();
			if (tokenStore.isLogin) {
				// alert(`로그인 되어있어, 구독 상태 최신화 함수 호출 시작`);
				if (isDeviceAOS) {
					try {
						// await mutateAosPaymentCheck();
						// 대가 리스트 최신화
						await getMasterListRefetch();
						// alert(`aos 구독 체크 에러 없이 호출 완료`);
					} catch (error) {
						// alert(`aos 구독 체크 실패 : ${JSON.stringify(error)}`);
					}
				}
				if (isDeviceIOS) {
					try {
						// await mutateIosPaymentCheck();
						// 대가 리스트 최신화
						await getMasterListRefetch();
						// alert(`ios 구독 체크 성공 : ${JSON.stringify(iosPaymentCheckedData.value)}`);
					} catch (error) {
						// alert(`구독 체크 실패 : ${JSON.stringify(error)}`);
					}
				}
			}
		},
	};

	const itId = setTimeout(() => {
		getPushPermissionYn();
		getPushToken();
		clearTimeout(itId);
	}, 3000);
});

onMounted(() => {
	window.addEventListener('storage', (event) => {
		if (event.key === 'token') {
			window.location.reload();
		}
	});
	getAppVersion(); // 앱 버전 체크 요청
	window.addEventListener('resize', displayStore.checkDisplay);
	window.addEventListener('resize', displayStore.checkSmallScreen);
});
onUnmounted(() => {
	window.removeEventListener('resize', displayStore.checkDisplay);
	window.addEventListener('resize', displayStore.checkSmallScreen);
});
</script>

<style scoped lang="scss"></style>
